import { FieldFunctionOptions, Reference, StoreObject } from '@apollo/client';

// Concatenate the incoming list items with existing list items
// see: https://www.apollographql.com/docs/react/caching/cache-field-behavior/#the-merge-function

type NamedItem = StoreObject | Reference;

const isObjectShape = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const namedMerger = (key: string) => {
  const namedMerge = (
    existing: Record<string, unknown> | undefined,
    incoming: Record<string, unknown> | undefined,
    { readField, mergeObjects }: FieldFunctionOptions,
  ) => {
    let merged: NamedItem[] = [];
    const existingItems = isObjectShape(existing) ? existing[key] : undefined;
    if (Array.isArray(existingItems)) {
      merged = (existingItems as NamedItem[]).slice(0);
    }

    const itemNameToIndex: Record<string, number> = Object.create(null);
    if (Array.isArray(existingItems)) {
      (existingItems as NamedItem[]).forEach((item, index) => {
        const itemName = readField<string>('name', item);
        if (itemName !== undefined) {
          itemNameToIndex[itemName] = index;
        }
      });
    }

    const incomingItems = isObjectShape(incoming) ? incoming[key] : undefined;
    if (Array.isArray(incomingItems)) {
      (incomingItems as NamedItem[]).forEach(item => {
        const itemName = readField<string>('name', item);
        if (itemName === undefined) {
          return;
        }

        const index = itemNameToIndex[itemName];
        if (index !== undefined) {
          // Merge the new item data with the existing author data.
          merged[index] = mergeObjects(merged[index], item);
        } else {
          // First time we've seen this item in this array.
          itemNameToIndex[itemName] = merged.length;
          merged.push(item);
        }
      });
    }

    return {
      ...incoming,
      [key]: merged,
    };
  };

  return namedMerge;
};
